import { queryOptions } from "@tanstack/react-query";
import { notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getApiClient, getSessionCookieHeader } from "~/utils/api-client";

export const SURVEYS_KEYS = {
	all: ["surveys"] as const,
	inbox: () => [...SURVEYS_KEYS.all, "inbox"] as const,
	detail: (run_id: string) => [...SURVEYS_KEYS.all, run_id] as const,
};

const RunIdSchema = z.object({
	run_id: z.string().min(1),
});

const fetchInboxSurveys = createServerFn({ method: "GET" }).handler(
	async () => {
		const api_client = getApiClient(getSessionCookieHeader());
		const result = await api_client.self.api.surveys.$get();

		if (!result.ok) {
			throw new Error("Failed to fetch surveys");
		}

		return await result.json();
	},
);

const fetchSurvey = createServerFn({ method: "GET" })
	.validator(RunIdSchema)
	.handler(async ({ data }) => {
		const api_client = getApiClient(getSessionCookieHeader());
		const result = await api_client.self.api.surveys[":run_id"].$get({
			param: { run_id: data.run_id },
		});

		if (!result.ok) {
			if (result.status === 404) {
				throw notFound();
			}

			throw new Error("Failed to fetch survey");
		}

		return await result.json();
	});

export const inboxSurveysQueryOptions = queryOptions({
	queryKey: SURVEYS_KEYS.inbox(),
	queryFn: () => fetchInboxSurveys(),
});

export const surveyDetailQueryOptions = (run_id: string) =>
	queryOptions({
		queryKey: SURVEYS_KEYS.detail(run_id),
		queryFn: () => fetchSurvey({ data: { run_id } }),
	});

export type InboxSurvey = Awaited<ReturnType<typeof fetchInboxSurveys>>[number];
export type SurveyDetail = Awaited<ReturnType<typeof fetchSurvey>>;
